import { useState, useEffect, useCallback } from 'react'

export interface UseResponseNavigationResult {
  currentIndex: number
  goPrev: () => void
  goNext: () => void
}

/**
 * State + keyboard handling for a "prev / next" navigator across N items.
 * Used by survey and interview insights pages to flip through responses.
 *
 * - Clamps currentIndex when total shrinks.
 * - Left/Right arrow keys advance (skipped when focus is in an editable field).
 */
export function useResponseNavigation(total: number): UseResponseNavigationResult {
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    if (total === 0) {
      setCurrentIndex(0)
    } else if (currentIndex >= total) {
      setCurrentIndex(total - 1)
    }
  }, [currentIndex, total])

  const goPrev = useCallback(() => {
    setCurrentIndex((i) => Math.max(0, i - 1))
  }, [])

  const goNext = useCallback(() => {
    setCurrentIndex((i) => Math.min(total - 1, i + 1))
  }, [total])

  useEffect(() => {
    if (total === 0) return
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        goPrev()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        goNext()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [goPrev, goNext, total])

  return { currentIndex, goPrev, goNext }
}
